import GooglePayButton from '@google-pay/button-react';
import { isInternalServerError } from '@payments-checkout/shared/helpers/constants/http-status.const';
import { isFailed } from '@payments-checkout/shared/helpers/constants/order-status.const';
import {
  DigitalWallets,
  DigitalWalletsStatusTypeAnaliticaEnum,
  StatusTypeAnaliticaEnum,
} from '@payments-checkout/shared/plugins/analitica/analitica.enum';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { reportError } from '@payments-checkout/shared/observability';
import {
  usePaymentMethod,
  useSessionId,
} from '@payments-checkout/shared/providers';
import { Spinner } from '@stone-payments/jade';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { usePageSharedData } from '../../hooks/usePageSharedData';
import {
  errorMessagePaymentGeneric
} from '../../components/pages/cardAddress/errorMessage';
import { buildGooglePayData } from '../helpers/googlePayActionButton.helpers';
import { useGooglePayData, useGooglePayment } from '../hooks';

export const GooglePayActionButton = ({ isValid }: { isValid: boolean }) => {
  const { sessionId } = useSessionId();
  const { setShowFailedPaymentAlert, setFailedPaymentMessage, installments } =
    usePaymentMethod();
  const { paymentRequest, googlePayData, isLoading, isSuccess } =
    useGooglePayData();
  const params = useParams<{ paymentLinkId: string }>();
  const { push } = useRouter();
  const { isRecurrence } = usePageSharedData();

  const installmentsRef = useRef(installments);

  useEffect(() => {
    installmentsRef.current = installments;
  }, [installments]);

  const { mutateAsync: sendPayment } = useGooglePayment({
    onSuccess: (data) => {
      if (!isFailed(data.order_status)) {
        if (isRecurrence) {
          push(`/${params.paymentLinkId}/credit-card-success`);
        } else {
          push(`/status/${sessionId}`);
        }
      } else {
        setShowFailedPaymentAlert(true);
        setFailedPaymentMessage(errorMessagePaymentGeneric(1000));
      }
    },
    onError: (error) => {
      const errorStatus = error?.response?.status ?? 500;
      reportError(error, {
        source: 'payment_submit',
        payment_method: 'google_pay',
      });

      setShowFailedPaymentAlert(true);
      setFailedPaymentMessage(
        isInternalServerError(errorStatus)
          ? errorMessagePaymentGeneric(1005)
          : errorMessagePaymentGeneric(1000)
      );
    },
  });

  if (isLoading) {
    return <Spinner size="medium" />;
  }

  if (!isSuccess) {
    return null;
  }
  if (!sessionId) return null;

  if (!isValid) {
    return (
      <div data-testid="disabled-gpay-button">
        <GooglePayButton
          buttonType="short"
          buttonColor="default"
          environment="TEST"
          paymentRequest={
            paymentRequest as unknown as google.payments.api.PaymentDataRequest
          }
        />
      </div>
    );
  }

  return (
    <GooglePayButton
      onCancel={() =>
        analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
          DigitalWalletsStatusTypeAnaliticaEnum.DESISTENCIA,
          DigitalWallets.GOOGLE_PAY
        )
      }
      buttonType="short"
      buttonColor="default"
      environment={googlePayData?.environment || 'TEST'}
      existingPaymentMethodRequired={
        googlePayData?.existing_payment_method_required
      }
      onClick={() =>
        analiticaInstance.events.homePage.submitData('googlepay')
      }
      paymentRequest={
        paymentRequest as unknown as google.payments.api.PaymentDataRequest
      }
      onPaymentAuthorized={(paymentData) => {
        return new Promise(function (resolve) {
          sendPayment({
            paymentData,
            paymentInfo: buildGooglePayData(
              paymentData.paymentMethodData.tokenizationData.token,
              installmentsRef.current,
              googlePayData?.merchant_info?.gateway_merchant_id
            ),
          })
            .then(function () {
              analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
                StatusTypeAnaliticaEnum.SUCCESS,
                DigitalWallets.GOOGLE_PAY
              );
              resolve({ transactionState: 'SUCCESS' });
            })
            // Sem `reportError` aqui: o `onError` do `usePayment` acima já
            // reportou este mesmo erro com `payment_submit`, e o dedupe é
            // first-writer-wins. Este catch existe para resolver com
            // `transactionState: 'ERROR'`, que a API do Google Pay exige.
            .catch(function () {
              analiticaInstance.events.homePage.digitalWalletsPaymentStatus(
                StatusTypeAnaliticaEnum.ERROR,
                DigitalWallets.GOOGLE_PAY
              );
              resolve({
                transactionState: 'ERROR',
                error: {
                  intent: 'PAYMENT_AUTHORIZATION',
                  message: 'An error occurred while processing the payment.',
                  reason: 'PAYMENT_DATA_INVALID',
                },
              });
            });
        });
      }}
    />
  );
};
